import React, { useState, useEffect } from 'react';
import {
  Typography,
  makeStyles,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
} from '@material-ui/core';
import { useAuth } from '../../../contexts/AuthContext';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 650,
    border: '1px solid #e0e0e0',
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(1.5),
  },
  shippingMethodCell: {
    padding: theme.spacing(1.5),
    width: '320px',
    maxWidth: '320px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  noOrdersMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loginMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  loginButton: {
    marginTop: theme.spacing(2),
  },
  orderNumber: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  status: {
    fontWeight: 'bold',
    '&.pending': {
      color: theme.palette.warning.main,
    },
    '&.completed': {
      color: theme.palette.success.main,
    },
    '&.cancelled': {
      color: theme.palette.error.main,
    },
  },
  errorMessage: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  highlightRow: {
    backgroundColor: '#e6f9ec', // very light green for ready/shipped
  },
  voidedRow: {
    backgroundColor: '#fdeaea', // very light red for voided
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  loadMoreButton: {
    minWidth: 120,
  },
}));

const OrderHistoryTab = () => {
  const classes = useStyles();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    hasMore: false,
    lastKey: null,
    limit: 25,
    count: 0,
  });
  const [copiedTrackingNumbers, setCopiedTrackingNumbers] = useState(new Set());

  const handleCopyTracking = (trackingNumber) => {
    if (!trackingNumber) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(trackingNumber);
      setCopiedTrackingNumbers((prev) => new Set([...prev, trackingNumber]));
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = trackingNumber;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedTrackingNumbers((prev) => new Set([...prev, trackingNumber]));
      } catch (err) {
        // Optionally handle error
      }
      document.body.removeChild(textArea);
    }
  };

  const fetchOrderHistory = async (isLoadMore = false) => {
    if (!isAuthenticated) return;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      // Clear copied tracking numbers when refreshing data
      setCopiedTrackingNumbers(new Set());
    }
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', pagination.limit.toString());

      if (isLoadMore && pagination.lastKey) {
        params.append('lastKey', pagination.lastKey);
      }

      const response = await fetch(`/api/auth/history?${params.toString()}`);
      const result = await response.json();
      console.log('result', result);

      if (result.success) {
        if (isLoadMore) {
          // Append new orders to existing ones
          setOrders((prevOrders) => [...prevOrders, ...result.data]);
        } else {
          // Replace orders for fresh load
          setOrders(result.data);
        }
        // Update pagination state
        setPagination({
          hasMore: result.pagination.hasMore,
          lastKey: result.pagination.lastKey,
          limit: result.pagination.limit,
          count: result.pagination.count,
        });
      } else {
        setError(result.message || 'Failed to fetch order history');
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
      setError('An error occurred while fetching your order history');
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleLoadMore = () => {
    fetchOrderHistory(true);
  };

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchOrderHistory();
    }
  }, [isAuthenticated, authLoading]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatOrderStatus = (order) => {
    const status = order.status;
    if (!status) return '';
    const statusPart = status.split('#')[1];
    if (statusPart === 'shipped' && order.tracking_number) {
      return (
        <Box component="span">
          Tracking:{' '}
          <a
            href={`https://www.google.com/search?q=${order.tracking_number}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {order.tracking_number}
          </a>
        </Box>
      );
    } else if (statusPart === 'emailed' && order.tracking_number) {
      return (
        <Box component="span">
          <a
            href={`https://locketprints.com/order-images/${order.tracking_number}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ready - View Files
          </a>
        </Box>
      );
    } else if (statusPart === 'ready') {
      return 'Order is complete';
    } else if (statusPart === 'voided') {
      return (
        <Typography
          variant="body2"
          style={{ color: '#d32f2f', fontWeight: 500 }}
        >
          Order voided
        </Typography>
      );
    } else {
      return '';
    }
  };

  const getStatusClass = (status) => {
    if (!status) return '';
    return status.toLowerCase();
  };

  if (authLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box className={classes.loginMessage}>
        <Typography variant="h6" gutterBottom>
          Sign In Required
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Please sign in on the account tab to view your order history.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.container}>
        <Box className={classes.errorMessage}>
          <Typography variant="body1">{error}</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => fetchOrderHistory()}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  if (orders.length === 0) {
    return (
      <Box className={classes.noOrdersMessage}>
        <Typography variant="h6" gutterBottom>
          No Orders Found
        </Typography>
        <Typography variant="body1" color="textSecondary">
          This system only shows orders associated with your online account,
          orders from before the SOS had this feature are not available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h6" gutterBottom>
        Order History
      </Typography>
      <Typography color="textSecondary" paragraph>
        Our system stores minimal order information, if you want to keep track
        of orders here we recommend that you use the Job Reference field when
        checking out.
      </Typography>
      <Typography color="textSecondary" paragraph>
        This information is as of the time of the order, so any changes made
        after the order is placed will not be reflected here.
      </Typography>
      <TableContainer>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableCell}>Order Number</TableCell>
              <TableCell className={classes.tableCell}>Date</TableCell>
              <TableCell className={classes.tableCell}>
                Shipping Method
              </TableCell>
              <TableCell className={classes.tableCell}>Job Ref</TableCell>
              <TableCell className={classes.tableCell}>Image Qty</TableCell>
              <TableCell className={classes.tableCell}>Order Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => {
              const statusPart =
                (order.status && order.status.split('#')[1]) || '';
              const isHighlight = ['ready', 'shipped'].includes(statusPart);
              const isVoided = statusPart === 'voided';
              return (
                <TableRow
                  key={order.orderNumber}
                  className={
                    isVoided
                      ? classes.voidedRow
                      : isHighlight
                      ? classes.highlightRow
                      : ''
                  }
                >
                  <TableCell className={classes.tableCell}>
                    <Typography className={classes.orderNumber}>
                      {order.orderNumber}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatDate(order.date)}
                  </TableCell>

                  <TableCell className={classes.shippingMethodCell}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        {order.shippingMethod || 'N/A'} <br />
                        {order.ship_type === 'pickup' ? (
                          ''
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            {order.ship_to || ''}
                          </Typography>
                        )}
                        {formatOrderStatus(order)}
                      </Box>
                      {order.status &&
                        order.status.split('#')[1] === 'shipped' &&
                        order.tracking_number && (
                          <Box display="flex" alignItems="center">
                            <Tooltip
                              title={
                                copiedTrackingNumbers.has(order.tracking_number)
                                  ? 'Copied!'
                                  : 'Copy tracking number to clipboard'
                              }
                            >
                              <IconButton
                                size="medium"
                                onClick={() =>
                                  handleCopyTracking(order.tracking_number)
                                }
                                style={{
                                  padding: 8,
                                  minWidth: 32,
                                  minHeight: 32,
                                }}
                                aria-label="Copy tracking number"
                              >
                                {copiedTrackingNumbers.has(
                                  order.tracking_number
                                ) ? (
                                  <CheckIcon fontSize="small" color="primary" />
                                ) : (
                                  <FileCopyIcon
                                    fontSize="small"
                                    color="inherit"
                                  />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {order.jobRef || '-'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {order.imageQty || 0}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatCurrency(order.orderTotal)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Load More Button or End Message */}
      {pagination.hasMore ? (
        <Box className={classes.loadMoreContainer}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className={classes.loadMoreButton}
          >
            {loadingMore ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </Box>
      ) : (
        orders.length > 0 && (
          <Box className={classes.loadMoreContainer}>
            <Typography variant="body2" color="textSecondary">
              No more orders to load
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default OrderHistoryTab;
